/**
 * §6.7 Multi-agent — role select, handoff-target multi-select over the
 * user's other agents (same-engine rule from §10 surfaced inline, per item
 * and in aggregate), and the handoff description the router reads.
 */

import { Field } from "../Field";
import { Input } from "../Input";
import { Select } from "../Select";
import { cx } from "../../lib/format";
import type { AgentConfig, AgentRole, AgentSummary } from "../../lib/types";
import { ENGINE_LABELS_SHORT } from "../../lib/types";
import type { DraftErrors } from "./validation";

const ROLE_OPTIONS: Array<{ value: AgentRole; label: string }> = [
  { value: "standalone", label: "Standalone — handles calls end to end" },
  { value: "router", label: "Router — greets callers and transfers to specialists" },
  { value: "specialist", label: "Specialist — receives transferred calls" },
];

export interface MultiAgentSectionProps {
  config: AgentConfig;
  patch: (partial: Partial<AgentConfig>) => void;
  errors: DraftErrors;
  role: AgentRole;
  onRoleChange: (role: AgentRole) => void;
  /** The user's agents minus this one; null while loading. */
  otherAgents: AgentSummary[] | null;
}

export function MultiAgentSection({
  config,
  patch,
  errors,
  role,
  onRoleChange,
  otherAgents,
}: MultiAgentSectionProps) {
  function toggleTarget(id: string) {
    const selected = config.handoff_targets.includes(id);
    patch({
      handoff_targets: selected
        ? config.handoff_targets.filter((t) => t !== id)
        : [...config.handoff_targets, id],
    });
  }

  return (
    <div className="flex flex-col gap-5">
      <Field
        label="Role"
        error={errors.role}
        help="Routers read the handoff descriptions of their targets to decide who takes the call."
      >
        <Select
          value={role}
          options={ROLE_OPTIONS}
          onChange={(event) => onRoleChange(event.target.value as AgentRole)}
        />
      </Field>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-body">
          Can transfer to
          {role === "router" && <span className="ml-1 text-muted">required for routers</span>}
        </span>
        {otherAgents === null ? (
          <p className="text-xs text-muted">Loading your agents…</p>
        ) : otherAgents.length === 0 ? (
          <p className="text-xs text-muted">
            No other agents yet — create a second agent to enable transfers.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded border border-line">
            {otherAgents.map((agent) => {
              const selected = config.handoff_targets.includes(agent.id);
              const engineMismatch = agent.voice_engine !== config.voice_engine;
              return (
                <li key={agent.id}>
                  <label
                    className={cx(
                      "flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors hover:bg-surface-raised",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleTarget(agent.id)}
                      className="h-3.5 w-3.5 accent-[color:var(--bright-hex)]"
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className={cx("truncate text-sm", selected ? "text-bright" : "text-body")}>
                        {agent.name}
                      </span>
                      {agent.description && (
                        <span className="truncate text-xs text-muted">
                          {agent.description}
                        </span>
                      )}
                    </span>
                    <span
                      className={cx(
                        "shrink-0 rounded border px-1.5 py-0.5 font-mono text-xs",
                        engineMismatch && selected
                          ? "border-danger/60 text-danger"
                          : "border-line text-muted",
                      )}
                    >
                      {ENGINE_LABELS_SHORT[agent.voice_engine]}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {errors.handoff_targets && (
          <p className="text-xs text-danger" role="alert">
            {errors.handoff_targets}
          </p>
        )}
        {!errors.handoff_targets && (otherAgents?.length ?? 0) > 0 && (
          <p className="text-xs text-muted">
            Transfers keep the conversation history. All agents in a transfer group must use the
            same engine.
          </p>
        )}
      </div>

      <Field
        label="When should calls come to this agent?"
        error={errors.handoff_description}
        trailing={
          <span className="font-mono">{(config.handoff_description ?? "").length} / 300</span>
        }
        help="One line a router reads when deciding to transfer here, e.g. “Billing questions and refund requests.”"
      >
        <Input
          value={config.handoff_description ?? ""}
          placeholder="e.g. Billing questions and refund requests"
          onChange={(event) => patch({ handoff_description: event.target.value || null })}
        />
      </Field>
    </div>
  );
}
